import { useDispatch, useSelector } from 'react-redux';
import { selectSessionAdvisor, selectAdvisorCarriers } from '@client/model/selectors';
import { useEffect, useMemo, useState } from 'react';
import {
  fetchCarriersByAdvisorIdRequest,
  postCarrierSettingsByAdvisorIdRequest,
} from '@client/model/advisorBgaCarrierSettingsModel';
import { InsuranceCarrier } from '@shared/insuranceCarrierDefs';

export function useCarrierSettings() {
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);
  const carriersState = useSelector(selectAdvisorCarriers);
  const initialSelectedProductsByCarrier = useMemo(() => {
    return carriersState.data.reduce(
      (acc, carrier) => ({
        ...acc,
        [carrier.insuranceCarrierId as InsuranceCarrier]: carrier.selectedPolicyTypes || [],
      }),
      {},
    );
  }, [carriersState.data]);

  const [selectedProductsByCarrier, setSelectedProductsByCarrier] = useState<
    Partial<Record<InsuranceCarrier, FundingPolicyType[]>>
  >(initialSelectedProductsByCarrier);

  const shouldFetchData = useMemo(() => {
    return advisor?.advisorId && carriersState.status === null && carriersState.data.length === 0;
  }, [advisor?.advisorId, carriersState]);

  useEffect(() => {
    setSelectedProductsByCarrier(initialSelectedProductsByCarrier);
  }, [initialSelectedProductsByCarrier]);

  useEffect(() => {
    if (shouldFetchData) {
      dispatch(fetchCarriersByAdvisorIdRequest({ advisorId: advisor.advisorId }));
    }
  }, [shouldFetchData, dispatch, advisor?.advisorId]);

  // Merge carrier data from DB with local selected products state
  const carriersWithSelectedProducts = useMemo(() => {
    return carriersState.data.map(carrier => ({
      ...carrier,
      selectedPolicyTypes:
        selectedProductsByCarrier[carrier.insuranceCarrierId as InsuranceCarrier] ||
        carrier.selectedPolicyTypes ||
        [],
    }));
  }, [carriersState.data, selectedProductsByCarrier]);

  const dispatchPostCarrierSettings = (
    updatedSettings: Partial<Record<InsuranceCarrier, FundingPolicyType[]>>,
  ) => {
    const carrierPolicySettings = Object.entries(updatedSettings).map(
      ([carrierId, selectedPolicyTypes]) => ({
        carrierId: Number(carrierId),
        selectedPolicyTypes: selectedPolicyTypes || [],
      }),
    );

    dispatch(
      postCarrierSettingsByAdvisorIdRequest({
        advisorId: advisor.advisorId,
        carrierPolicySettings,
      }),
    );
  };

  const updateProductSelection = (
    carrierId: InsuranceCarrier,
    product: FundingPolicyType,
    action: 'toggle' | 'remove' | 'add',
  ) => {
    const carrier = carriersState.data.find(c => c.insuranceCarrierId === carrierId);
    if (!carrier) return;

    const currentSelected =
      selectedProductsByCarrier[carrierId] || carrier.selectedPolicyTypes || [];

    let updatedSelected: FundingPolicyType[];

    switch (action) {
      case 'toggle':
        updatedSelected = currentSelected.includes(product)
          ? currentSelected.filter(p => p !== product)
          : [...currentSelected, product];
        break;
      case 'remove':
        updatedSelected = currentSelected.filter(p => p !== product);
        break;
      case 'add':
        updatedSelected = currentSelected.includes(product)
          ? currentSelected
          : [...currentSelected, product];
        break;
    }

    const newSettings = {
      ...selectedProductsByCarrier,
      [carrierId]: updatedSelected,
    };

    setSelectedProductsByCarrier(newSettings);

    // Dispatch bulk update with all carrier settings
    dispatchPostCarrierSettings(newSettings);
  };

  // Convenience methods that use the unified function
  const toggleProduct = (carrierId: InsuranceCarrier, product: FundingPolicyType) => {
    updateProductSelection(carrierId, product, 'toggle');
  };

  const removeProduct = (carrierId: InsuranceCarrier, product: FundingPolicyType) => {
    updateProductSelection(carrierId, product, 'remove');
  };

  const initializeCarrierSettings = (carrierId: InsuranceCarrier) => {
    const carrier = carriersState.data.find(c => c.insuranceCarrierId === carrierId);
    if (!carrier) return;

    // Initialize with existing selected products from DB or empty array
    setSelectedProductsByCarrier(prev => ({
      ...prev,
      [carrierId]: carrier.selectedPolicyTypes || [],
    }));
  };

  return {
    carriers: carriersWithSelectedProducts, // Carriers with merged selected products
    isLoading: carriersState.status === 'loading',
    toggleProduct,
    removeProduct,
    initializeCarrierSettings,
  };
}
